 import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client";

/**
 * useGeneratedSqlFeedback – hook for generated SQL feedback.
 * - AddGeneratedSqlFeedback mutation for positive/negative feedback.
 * - Follows same pattern as useRole (Apollo useMutation, error handling).
 */

const ADD_GENERATED_SQL_FEEDBACK = gql`
  mutation AddGeneratedSqlFeedback($input: AddGeneratedSqlFeedbackInput!) {
    AddGeneratedSqlFeedback(input: $input) {
      generated_sql_feedback_id
      question
      generated_sql
      expected_sql
      type
      feedback
      comment
      model_id
      raised_by
      is_approved
      is_active
      created_on
      updated_on
    }
  }
`;

export const useGeneratedSqlFeedback = () => {
  const [addGeneratedSqlFeedbackMutation] = useMutation(ADD_GENERATED_SQL_FEEDBACK);

  /**
   * Adds a generated SQL feedback (e.g. on thumb up / thumb down).
   * @param {Object} params
   * @param {string} params.question - User question (base or refined/follow-up).
   * @param {string} params.generated_sql - The generated SQL.
   * @param {string} [params.expected_sql] - Expected SQL (optional; for thumb up can equal generated_sql).
   * @param {string} [params.type] - One of: query, metric, join, correction, filter, aggregation. Omit for thumb down.
   * @param {string} [params.raised_by] - User id (e.g. from StorageHelper.get("userDetails")?.user_id).
   * @param {number} [params.nlp_message_id] - NLP message id (stored in generated_sql_feedbacks and used to set feedback on nlp_messages).
   * @param {number|string} [params.user_db_id] - User DB/connection id (e.g. StorageHelper.get("selected_user_db_id")).
   * @param {string} [params.feedback] - Thumb feedback: "good" (thumb up) | "poor" (thumb down).
   * @param {string} [params.db_type] - Database type (e.g. "snowflake" or "postgres").
   * @param {string} [params.comment] - Optional user comment explaining the feedback.
   * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
   */
  const addGeneratedSqlFeedback = async ({
    question,
    generated_sql,
    expected_sql = null,
    type,
    raised_by = null,
    nlp_message_id = null,
    user_db_id = null,
    feedback = null,
    comment = null,
    model_id = null,
  }) => {
    try {
      const input = {
        question: question ?? "",
        generated_sql: generated_sql ?? "",
        expected_sql: expected_sql ?? null,
        raised_by: raised_by != null ? String(raised_by) : null,
        db_type: "snowflake"
      };
      if (type != null && type !== "") {
        input.type = type;
      }
      if (nlp_message_id != null) {
        input.nlp_message_id = Number(nlp_message_id);
      }
      if (user_db_id != null && user_db_id !== "") {
        input.user_db_id = Number(user_db_id);
      }
      if (feedback != null && feedback !== "") {
        input.feedback = String(feedback);
      }
      if (comment != null && comment !== "") {
        input.comment = String(comment);
      }
      if (model_id != null && model_id !== "") {
        input.model_id = String(model_id);
      }
      const { data, errors } = await addGeneratedSqlFeedbackMutation({
        variables: { input },
      });

      if (errors?.length > 0) {
        throw new Error(errors[0].message);
      }

      if (!data?.AddGeneratedSqlFeedback) {
        throw new Error("Failed to add generated SQL feedback");
      }

      return {
        success: true,
        data: data.AddGeneratedSqlFeedback,
      };
    } catch (error) {
      if (error.graphQLErrors?.length > 0) {
        const graphQLError = error.graphQLErrors[0];
        throw new Error(graphQLError.message);
      }
      if (error.networkError?.result?.errors?.length > 0) {
        const networkError = error.networkError.result.errors[0];
        throw new Error(networkError.message);
      }
      throw error;
    }
  };

  return {
    addGeneratedSqlFeedback,
  };
};
